import Bowser from "bowser";
import { APP_VERSION } from "#app/lib/app-version";

/**
 * What this client is running on, stamped onto every analytics event.
 *
 * The field names match the feedback context (`use-submit-feedback.ts`) and the
 * native apps, so the same device is described with the same words wherever it
 * shows up. The analytics service validates this shape and rejects keys it does
 * not know, so an added field needs its DTO counterpart first.
 *
 * `deviceModel` is deliberately absent: the web platform does not expose one,
 * and a guess derived from the user agent would read as fact.
 */
// `exactOptionalPropertyTypes` is on, so an optional field that is assigned
// progressively (rather than spread in) must admit `undefined` as a value, not
// only as an absent key. JSON.stringify drops the undefined ones on the way
// out, which is what keeps the payload to the fields this device can answer.
export type DeviceContext = {
  platform: "web";
  appVersion: string;
  osName?: string | undefined;
  osVersion?: string | undefined;
  browserName?: string | undefined;
  browserVersion?: string | undefined;
  viewport?: string | undefined;
  scale?: number | undefined;
  locale?: string | undefined;
  timezone?: string | undefined;
  connection?: string | undefined;
  userAgent?: string | undefined;
};

type NetworkInformation = { effectiveType?: string };

type UserAgentData = {
  getHighEntropyValues: (hints: string[]) => Promise<{ platformVersion?: string }>;
};

/**
 * The OS version the user agent string reports, corrected by a
 * `platformVersion` client hint where the UA is known to lie.
 *
 * Windows is the case that matters: the UA string says "10" for Windows 10 and
 * Windows 11 alike, and only the high-entropy hint separates them — major 13
 * and above is 11, 1 through 12 is 10. Android's UA is truncated on some
 * builds, so the hint wins there too.
 */
export function correctOsVersion(
  osName: string | undefined,
  uaVersion: string | undefined,
  platformVersionHint: string | null | undefined,
): string | undefined {
  if (!platformVersionHint) return uaVersion;
  const os = osName?.toLowerCase();
  if (os === "android") return platformVersionHint;
  if (os === "windows") {
    const major = parseInt(platformVersionHint, 10);
    if (major >= 13) return "11";
    if (major >= 1) return "10";
    return platformVersionHint;
  }
  return uaVersion;
}

/**
 * Read the device once. Every accessor here can throw or be missing in a
 * hardened browser, so each is guarded independently — a blocked timezone must
 * not cost us the platform.
 */
export function resolveDeviceContext(
  platformVersionHint?: string | null,
): DeviceContext {
  const device: DeviceContext = { platform: "web", appVersion: APP_VERSION };

  try {
    const browser = Bowser.getParser(navigator.userAgent);
    device.osName = browser.getOSName() || undefined;
    device.osVersion = correctOsVersion(
      device.osName,
      browser.getOSVersion() || undefined,
      platformVersionHint,
    );
    device.browserName = browser.getBrowserName() || undefined;
    device.browserVersion = browser.getBrowserVersion() || undefined;
    device.userAgent = navigator.userAgent;
  } catch {
    // An unparseable user agent still leaves platform and appVersion usable.
  }

  try {
    device.viewport = `${window.innerWidth}x${window.innerHeight}`;
    device.scale = window.devicePixelRatio;
  } catch {
    // Non-browser render targets have no window.
  }

  try {
    device.locale = navigator.language;
    device.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Locale and timezone are both blockable by privacy settings.
  }

  try {
    const connection = (navigator as Navigator & { connection?: NetworkInformation })
      .connection;
    device.connection = connection?.effectiveType;
  } catch {
    // Network Information is not implemented in Safari or Firefox.
  }

  return device;
}

/**
 * The device as one display line, e.g. "macOS 15 • Chrome 131". Shown in the
 * help menu and copied into support threads.
 */
export function formatDeviceLabel(device: DeviceContext): string {
  const os = [device.osName, device.osVersion].filter(Boolean).join(" ");
  const browser = [device.browserName, device.browserVersion].filter(Boolean).join(" ");
  const parts = [os, browser].filter(Boolean);
  return parts.length > 0 ? parts.join(" • ") : "Web";
}

let cached: DeviceContext | null = null;

/**
 * The memoized device for the life of the tab. Resolved on first read rather
 * than at import so a test can drive `resolveDeviceContext` directly, and
 * refined in place once the browser answers the high-entropy hint request —
 * events sent in the first moments carry the uncorrected OS version, which is
 * the same trade the help menu already makes.
 */
export function getDeviceContext(): DeviceContext {
  if (cached) return cached;
  cached = resolveDeviceContext();

  const userAgentData = (navigator as Navigator & { userAgentData?: UserAgentData })
    .userAgentData;
  userAgentData
    ?.getHighEntropyValues(["platformVersion"])
    .then((hints) => {
      if (!cached || !hints.platformVersion) return;
      cached.osVersion = correctOsVersion(
        cached.osName,
        cached.osVersion,
        hints.platformVersion,
      );
    })
    .catch(() => {});

  return cached;
}
